import { useRef } from "react";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { MaskLines } from "@/components/site/Motion";
import { scrollToId, EASE_OUT } from "@/lib/scroll";

const fade = (delay) => ({
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: EASE_OUT },
});

const HeroVisual = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [8, -8]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 18 });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div ref={ref} className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset} data-testid="hero-visual">
      <motion.div
        initial={{ clipPath: "inset(100% 0% 0% 0% round 240px 240px 24px 24px)" }}
        animate={{ clipPath: "inset(0% 0% 0% 0% round 240px 240px 24px 24px)" }}
        transition={{ duration: 1.6, delay: 0.5, ease: [0.76, 0, 0.24, 1] }}
        style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
        className="relative mx-auto aspect-[4/5] w-full max-w-[460px] overflow-hidden bg-[#0c1222]"
      >
        <svg className="absolute h-0 w-0" aria-hidden>
          <filter id="flag-wave">
            <feTurbulence type="fractalNoise" baseFrequency="0.006 0.012" numOctaves="2" seed="3">
              <animate attributeName="baseFrequency" dur="9s" values="0.006 0.012;0.009 0.016;0.006 0.012" repeatCount="indefinite" />
            </feTurbulence>
            <feDisplacementMap in="SourceGraphic" scale="9" />
          </filter>
        </svg>
        <div className="flag-sway absolute inset-0" data-testid="hero-flag">
          <motion.img
            src="/assets/flag.jpg"
            alt="Drapeau UNDERCLEAN"
            style={{ y: imgY, scale: 1.25, filter: "url(#flag-wave)" }}
            className="absolute inset-0 h-full w-full object-cover object-[50%_20%]"
          />
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,transparent_35%,rgba(5,8,17,0.8)_90%)]" />
        <div className="absolute bottom-6 left-6 right-6 flex items-end justify-end">
          <div className="font-mono-uc text-[11px] uppercase tracking-[0.2em] text-slate-200">
            Propreté
            <br />
            <span className="text-cyan-300">& services</span>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, scale: 0.6, rotate: -40 }}
        animate={{ opacity: 1, scale: 1, rotate: 0 }}
        transition={{ duration: 1.4, delay: 1.3, ease: EASE_OUT }}
        className="absolute -left-2 sm:-left-8 bottom-10 h-32 w-32 sm:h-40 sm:w-40"
      >
        <svg viewBox="0 0 200 200" className="absolute inset-0 spin-slow">
          <defs>
            <path id="ring" d="M100,100 m-78,0 a78,78 0 1,1 156,0 a78,78 0 1,1 -156,0" />
          </defs>
          <text className="font-mono-uc" fontSize="13" letterSpacing="5" fill="#0af5f0">
            <textPath href="#ring">PROPRETÉ ET SERVICES • DEVIS GRATUIT • </textPath>
          </text>
        </svg>
        <img src="/assets/logo-mark.png" alt="" className="absolute left-1/2 top-1/2 h-[58%] w-[58%] -translate-x-1/2 -translate-y-1/2 rounded-full object-cover shadow-[0_0_40px_rgba(10,245,240,0.35)]" data-testid="hero-badge-logo" />
      </motion.div>
    </div>
  );
};

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const wordX = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden pt-28 sm:pt-32" data-testid="hero-section">
      <div className="pointer-events-none absolute -top-40 -left-40 h-[520px] w-[520px] rounded-full bg-[#22a6ff]/20 blur-[140px]" />
      <div className="pointer-events-none absolute top-1/3 right-0 h-[420px] w-[420px] rounded-full bg-[#5aec5a]/10 blur-[140px]" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.1)} className="font-mono-uc text-xs uppercase tracking-[0.2em] leading-relaxed text-cyan-300 flex items-center gap-3">
            <span className="h-2 w-2 rounded-full bg-[#5aec5a] shadow-[0_0_12px_#5aec5a]" />
            <span data-testid="hero-welcome-offer">Offre de bienvenue : UNDERCLEAN vous offre -20 % sur la première intervention</span>
          </motion.div>

          <h1 className="mt-7 font-display font-semibold tracking-tight text-white text-[2.9rem] leading-[0.98] sm:text-7xl lg:text-[5.6rem]" data-testid="hero-title">
            <MaskLines
              delay={0.25}
              lines={[
                "La propreté",
                "se juge dans",
                <span key="s" className="font-serif-i text-brand pr-2">le détail.</span>,
              ]}
            />
          </h1>

          <motion.p {...fade(0.9)} className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed">
            UNDERCLEAN prend soin de vos bureaux, locaux professionnels, bâtiments et surfaces vitrées.
            Un nettoyage précis, discret et régulier, pour des espaces qui donnent envie d'y travailler.
          </motion.p>

          <motion.div {...fade(1.05)} className="mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollToId("devis")}
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-brand px-8 py-4 font-semibold text-[#050811] hover:shadow-[0_0_48px_rgba(10,245,240,0.5)] transition-shadow duration-500"
              data-testid="hero-cta-devis"
            >
              Obtenir mon devis gratuit
              <ArrowUpRight className="h-5 w-5 transition-transform duration-500 group-hover:rotate-45" />
            </button>
            <button
              onClick={() => scrollToId("services")}
              className="group inline-flex items-center justify-center gap-3 rounded-full border border-cyan-300/30 px-8 py-4 text-white hover:border-cyan-300 hover:bg-cyan-300/5 transition-[border-color,background-color] duration-300"
              data-testid="hero-cta-services"
            >
              Nos prestations
              <ArrowDownRight className="h-5 w-5 transition-transform duration-500 group-hover:translate-y-1" />
            </button>
          </motion.div>

          <motion.div {...fade(1.2)} className="mt-14 grid grid-cols-3 max-w-lg border-t border-white/10">
            {[
              ["Devis", "gratuit"],
              ["Réponse", "sous 48 h"],
              ["Prestations", "sur mesure"],
            ].map(([a, b]) => (
              <div key={a} className="pt-5 pr-3">
                <div className="font-mono-uc text-[11px] uppercase tracking-[0.18em] text-slate-400">{a}</div>
                <div className="mt-1 font-display text-lg sm:text-xl text-brand">{b}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <HeroVisual />
        </div>
      </div>

      <motion.div
        style={{ x: wordX }}
        className="pointer-events-none select-none mt-16 sm:mt-20 whitespace-nowrap font-display font-extrabold text-outline text-[22vw] leading-[0.8] tracking-tighter"
        aria-hidden
      >
        UNDERCLEAN UNDERCLEAN
      </motion.div>
    </section>
  );
}
